import React, { useState } from 'react';
import { TopBar } from '../components/TopBar';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Phone, CreditCard, Lock, UserX, Shield, DollarSign, Smartphone, Monitor, Users, ChevronDown, ChevronUp, AlertTriangle, CheckCircle, ExternalLink } from 'lucide-react';
// Scenario data structure
const SCENARIOS = [{
  id: 'scam-call',
  icon: Phone,
  title: 'Someone is calling or texting me claiming to be from my bank, the IRS, or another trusted organization',
  likelyHappening: 'This is probably a scam. According to the FBI, phishing was the most reported cybercrime in 2024, with over 193,000 complaints and $70 million in losses. Scammers impersonate banks, government agencies, and tech companies to steal your information or money.',
  immediateActions: [{
    title: "Hang up or don't respond.",
    desc: "Real organizations won't pressure you or ask for passwords, PINs, or one-time codes over the phone."
  }, {
    title: 'Contact them directly.',
    desc: "Look up the official phone number yourself (don't use any number they gave you). Call the organization using the number on your card, their website, or your official paperwork."
  }, {
    title: "Don't click any links in texts.",
    desc: 'Delete the message. If you need to check something, go directly to the official website or app.'
  }],
  whyItMatters: 'Even if the call looks legitimate (the caller ID can be faked), real banks and government agencies will never ask for sensitive information this way. If they claim your account is compromised or you owe money, they can wait for you to call them back through official channels.',
  watchForNext: 'Monitor your accounts for the next few days. If you accidentally shared information, follow the steps under "I think I gave my password or personal information to a scammer" below.'
}, {
  id: 'unauthorized-charges',
  icon: CreditCard,
  title: "I see charges on my credit card or bank account I didn't make",
  likelyHappening: 'Someone got your card information and is making unauthorized purchases. This happens to millions of people and the faster you act, the better your chances of getting your money back.',
  immediateActions: [{
    title: 'Call your bank or credit card company right now.',
    desc: 'Use the number on the back of your card. Most will detect fraud before you do and can freeze your account immediately.'
  }, {
    title: 'Dispute the charges.',
    desc: "Write down the transaction details (date, amount, merchant) and tell your bank you didn't authorize them."
  }, {
    title: 'Request a new card.',
    desc: "They'll send you a replacement with a new number."
  }, {
    title: 'Update automatic payments.',
    desc: 'Once you get your new card, update any subscriptions or bills tied to the old card number.'
  }],
  whyItMatters: "Federal law limits your liability for credit card fraud to $50 (and most banks charge $0). For debit cards, you're protected if you report within 60 days. The sooner you report, the less you'll lose.",
  watchForNext: "Check your other accounts to make sure no other cards were compromised. Sign up for transaction alerts on all your accounts so you'll know immediately if something suspicious happens again."
}, {
  id: 'locked-out',
  icon: Lock,
  title: "I can't log into my account (email, social media, or something else)",
  likelyHappening: "Either you forgot your password, or someone changed it to lock you out. If you didn't request a password reset and you're suddenly locked out, your account may have been compromised.",
  immediateActions: [{
    title: 'Try the password reset option.',
    desc: 'Most sites have a "Forgot Password?" link. Check your email for the reset link.'
  }, {
    title: 'Check your email for alerts.',
    desc: "Look for messages about password changes or login attempts from locations you don't recognize."
  }, {
    title: 'Verify your recovery information.',
    desc: "Make sure your backup email and phone number are still yours. If the hacker changed them, contact the platform's support team immediately."
  }, {
    title: 'Review active sessions.',
    desc: "If you regain access, check for devices or locations you don't recognize and log them out."
  }],
  subSections: [{
    title: 'If you can get back in:',
    points: ['Change your password immediately. Use a strong, unique password.', 'Turn on two-factor authentication (2FA).', 'Check what changed (forwarding rules, posts, settings).']
  }, {
    title: "If you can't get back in:",
    points: ["Contact the platform's support team immediately.", "Warn your contacts so they don't fall for scams sent from your account."]
  }],
  whyItMatters: "Your email account is the master key to your digital life. If someone controls your email, they can reset passwords for your other accounts. That's why securing this quickly is so important.",
  watchForNext: 'Monitor your accounts for suspicious activity over the next few weeks. Change passwords on any other accounts that used the same password.'
}, {
  id: 'phishing-victim',
  icon: UserX,
  title: 'I think I gave my password or personal information to a scammer',
  likelyHappening: "You may have responded to a phishing email, text, or call. Scammers are incredibly convincing, they fake logos, use urgent language, and create websites that look exactly like the real thing. You're not alone. According to Pew Research, 73% of Americans have experienced some form of online scam.",
  immediateActions: [{
    title: 'Change your password right away.',
    desc: 'Do this from a different device if possible, or at least from a secure computer.'
  }, {
    title: 'Turn on two-factor authentication (2FA).',
    desc: 'This adds an extra layer of protection even if someone has your password.'
  }, {
    title: 'Check if you used that password anywhere else.',
    desc: 'If you reused the same password on other accounts, change those immediately too.'
  }, {
    title: 'Contact your bank if you shared financial information.',
    desc: 'Call them right away. They can monitor your accounts and freeze them if needed.'
  }, {
    title: 'Monitor your accounts.',
    desc: "Watch for unfamiliar transactions, password reset emails, or login alerts from locations you don't recognize."
  }],
  whyItMatters: 'The damage from a phishing scam depends on what you shared. Passwords can be changed quickly. But if you shared your Social Security number, bank details, or answers to security questions, you\'ll need to take additional steps (see "Identity theft concerns" below).',
  watchForNext: 'Stay alert for follow-up scams. Scammers often sell stolen information, so you might get more phishing attempts. Be suspicious of any unexpected messages.'
}, {
  id: 'identity-theft',
  icon: Shield,
  title: "I'm worried about identity theft",
  likelyHappening: "Identity theft occurs when someone uses your personal information (Social Security number, driver's license, credit card) to open accounts, file taxes, or make purchases in your name. According to the FBI, over 60-year-olds reported nearly $5 billion in losses to fraud in 2024.",
  immediateActions: [{
    title: 'Place a fraud alert on your credit.',
    desc: "Call one of the three major credit bureaus (Equifax, Experian, or TransUnion). When you notify one, they're required to notify the others."
  }, {
    title: 'Consider a credit freeze.',
    desc: "This completely blocks access to your credit report. You'll need to contact all three bureaus separately: Equifax (800-685-1111), Experian (888-397-3742), TransUnion (888-909-8872)."
  }, {
    title: 'Check your credit reports.',
    desc: "Get free weekly credit reports at AnnualCreditReport.com. Look for accounts you don't recognize."
  }, {
    title: 'Report the theft.',
    desc: "File a report at IdentityTheft.gov (the FTC's official site). This creates a recovery plan."
  }, {
    title: 'File a police report.',
    desc: 'Some creditors and banks require this as proof of identity theft.'
  }],
  whyItMatters: 'Identity theft can take months or years to fully resolve. The faster you act, the less damage can be done. According to the FTC, the average victim spends $1,400 and countless hours recovering from identity theft.',
  watchForNext: "Monitor your credit reports regularly for at least a year. Sign up for alerts from your bank and credit card companies. Be very suspicious of any mail about accounts you didn't open."
}, {
  id: 'money-scam',
  icon: DollarSign,
  title: 'Someone asked me to send money urgently (by wire transfer, gift cards, or cryptocurrency)',
  likelyHappening: "This is almost certainly a scam. Legitimate organizations don't ask for payment via gift cards, wire transfers, or cryptocurrency. Investment scams cost victims over $6.5 billion in 2024 according to the FBI.",
  immediateActions: [{
    title: 'STOP. Do not send money.',
    desc: "It doesn't matter how urgent they say it is or how convincing they sound. Real emergencies don't require gift cards or untraceable payments."
  }, {
    title: 'Verify the request through another method.',
    desc: "If it's supposedly from a family member, call them directly using a phone number you already have. If it's from a company, hang up and call their official number."
  }],
  subSections: [{
    title: 'If you already sent money:',
    points: ['Gift cards: Contact the company (Google, Apple, Amazon) immediately.', 'Wire transfer: Contact your bank or Western Union/MoneyGram immediately.', 'Cryptocurrency: Contact the exchange you used and report the fraud.']
  }],
  whyItMatters: "Once money is sent via these methods, it's almost impossible to recover. Scammers know this, which is why they pressure you to act fast.",
  watchForNext: "Scammers often target the same people repeatedly. If you fell for this once, you'll likely be contacted again. Be extremely skeptical of any unexpected requests."
}, {
  id: 'clicked-link',
  icon: Smartphone,
  title: 'I got a text or email with a link and I clicked it',
  likelyHappening: "You may have clicked a phishing link designed to steal your information or install malware on your device. Not all links are malicious, but if you're worried, here's what to do.",
  immediateActions: [{
    title: "Don't enter any information.",
    desc: 'If the link took you to a page asking for login credentials, close it immediately.'
  }, {
    title: 'Change passwords if you entered any information.',
    desc: 'If you typed in a username or password, change those passwords right away on the legitimate website.'
  }, {
    title: 'Run a security scan.',
    desc: 'Update your antivirus software and run a full scan on your device.'
  }, {
    title: 'Watch for suspicious behavior.',
    desc: 'Look for unexpected password reset emails, slower device performance, or unfamiliar apps.'
  }],
  whyItMatters: "Clicking a link alone usually won't infect your device, but entering information on a fake site will. Malicious links can also automatically download malware.",
  watchForNext: 'Be cautious of follow-up messages. Scammers often send multiple messages trying different approaches.'
}, {
  id: 'computer-virus',
  icon: Monitor,
  title: 'My computer is acting strange or I got a pop-up saying I have a virus',
  likelyHappening: "You might have malware, or you're seeing a fake virus warning designed to scare you into calling a scam tech support number. Real antivirus software doesn't work through pop-ups that ask you to call a number.",
  immediateActions: [{
    title: "Don't call any phone numbers in the pop-up.",
    desc: 'These are scams. Close the pop-up or restart your computer.'
  }, {
    title: 'Don\'t click "OK" or "Remove virus" in suspicious pop-ups.',
    desc: 'These buttons might install actual malware. Instead, close your browser using Task Manager or Force Quit.'
  }, {
    title: 'Update your antivirus software.',
    desc: "Run a full scan. If you don't have one, use Windows Defender."
  }, {
    title: 'Check for unfamiliar programs.',
    desc: "Uninstall anything you don't recognize or didn't intentionally install."
  }],
  whyItMatters: 'Tech support scams cost Americans hundreds of millions annually. The "technician" will either install real malware while pretending to fix your computer or charge you for unnecessary services.',
  watchForNext: "If your computer was infected, change your passwords after you've cleaned it. Monitor your accounts for suspicious activity."
}, {
  id: 'impersonation',
  icon: Users,
  title: 'Someone is impersonating me or a family member online',
  likelyHappening: 'Scammers create fake social media profiles using stolen photos and information to trick your friends and family into sending money or personal information.',
  immediateActions: [{
    title: 'Alert your friends and family.',
    desc: 'Send a message (using a method you know is really you) letting them know about the fake account.'
  }, {
    title: 'Report the fake account.',
    desc: "Use the platform's reporting tools (Facebook, Instagram, X) to report impersonation."
  }, {
    title: 'Document everything.',
    desc: "Take screenshots of the fake profile and any messages they've sent."
  }, {
    title: 'Check your privacy settings.',
    desc: "Make sure your real accounts aren't public."
  }, {
    title: 'Change your passwords.',
    desc: 'If they have your photos and information, they might have tried to access your actual accounts.'
  }],
  whyItMatters: 'Scammers use these fake accounts for "friend in need" scams, asking your contacts for money. They\'re convincing because they have your photos.',
  watchForNext: 'Check periodically to make sure the fake account stays down. Consider making your social media accounts more private.'
}];
function ScenarioCard({
  scenario
}: {
  scenario: (typeof SCENARIOS)[0];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const Icon = scenario.icon;
  return <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden mb-4 shadow-sm hover:shadow-md transition-shadow" data-id="element-917">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-start p-6 text-left focus:outline-none" data-id="element-918">
        <div className={`p-3 rounded-lg mr-4 flex-shrink-0 ${isOpen ? 'bg-[#631bb7] text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-919">
          <Icon size={24} data-id="element-920" />
        </div>
        <div className="flex-grow pr-4" data-id="element-921">
          <h3 className="text-lg md:text-xl font-bold text-[#111111] leading-tight" data-id="element-922">
            {scenario.title}
          </h3>
        </div>
        <div className="flex-shrink-0 mt-1" data-id="element-923">
          {isOpen ? <ChevronUp className="text-gray-400" data-id="element-924" /> : <ChevronDown className="text-gray-400" data-id="element-925" />}
        </div>
      </button>

      {isOpen && <div className="px-6 pb-8 md:px-8 md:pb-10 border-t border-gray-100" data-id="element-926">
          {/* What's Happening */}
          <div className="mt-6 mb-8" data-id="element-927">
            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-2" data-id="element-928">
              What's likely happening
            </h4>
            <p className="text-gray-700 leading-relaxed" data-id="element-929">
              {scenario.likelyHappening}
            </p>
          </div>

          {/* Immediate Actions */}
          <div className="bg-red-50 border border-red-100 rounded-xl p-6 mb-8" data-id="element-930">
            <div className="flex items-center mb-4" data-id="element-931">
              <AlertTriangle className="text-red-600 mr-2" size={20} data-id="element-932" />
              <h4 className="text-lg font-bold text-red-700" data-id="element-933">
                Do this immediately
              </h4>
            </div>
            <ul className="space-y-4" data-id="element-934">
              {scenario.immediateActions.map((action, idx) => <li key={idx} className="flex items-start" data-id="element-935">
                  <div className="min-w-[24px] h-6 flex items-center justify-center bg-red-200 text-red-800 rounded-full text-xs font-bold mr-3 mt-0.5" data-id="element-936">
                    {idx + 1}
                  </div>
                  <div data-id="element-937">
                    <span className="font-bold text-gray-900 block mb-1" data-id="element-938">
                      {action.title}
                    </span>
                    <span className="text-gray-700" data-id="element-939">{action.desc}</span>
                  </div>
                </li>)}
            </ul>
          </div>

          {/* Subsections if any */}
          {scenario.subSections?.map((sub, idx) => <div key={idx} className="mb-8" data-id="element-940">
              <h4 className="font-bold text-gray-900 mb-3" data-id="element-941">{sub.title}</h4>
              <ul className="list-disc pl-5 space-y-2 text-gray-700" data-id="element-942">
                {sub.points.map((point, pIdx) => <li key={pIdx} data-id="element-943">{point}</li>)}
              </ul>
            </div>)}

          {/* Why this matters & What to watch for */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-944">
            <div className="bg-gray-50 rounded-lg p-5" data-id="element-945">
              <h4 className="font-bold text-gray-900 mb-2" data-id="element-946">Why this matters</h4>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-947">
                {scenario.whyItMatters}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-5" data-id="element-948">
              <h4 className="font-bold text-gray-900 mb-2" data-id="element-949">
                What to watch for next
              </h4>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-950">
                {scenario.watchForNext}
              </p>
            </div>
          </div>
        </div>}
    </div>;
}
export function CrisisResponsePage() {
  return <div className="flex flex-col min-h-screen w-full bg-white font-inter" data-id="element-951">
      <TopBar data-id="element-952" />
      <Header data-id="element-953" />

      {/* Hero */}
      <section className="bg-[#1a0647] text-white py-16 md:py-24" data-id="element-954">
        <div className="max-w-4xl mx-auto px-6" data-id="element-955">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12" data-id="element-956">
            {/* Copy */}
            <div className="flex-1 text-center md:text-left" data-id="element-957">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-958">
                Crisis Response Guide
              </h1>
              <p className="text-lg md:text-xl text-gray-300 leading-relaxed max-w-2xl" data-id="element-959">
                Select the situation that best describes what's happening. We'll
                walk you through exactly what to do, step by step.
              </p>
            </div>
            {/* Hacker Illustration */}
            <div className="flex-shrink-0 w-40 md:w-56" data-id="element-960">
              <img src="https://cdn.magicpatterns.com/uploads/eRruBHucmBW4VxMXi1k2eP/Hacker.png" alt="Hacker illustration" className="w-full h-auto drop-shadow-2xl" data-id="element-961" />
            </div>
          </div>
        </div>
      </section>

      {/* How to use */}
      <section className="py-12 bg-white border-b border-gray-100" data-id="element-962">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-963">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-964">
            How to Use This Guide
          </h2>
          <p className="text-lg text-gray-600" data-id="element-965">
            Answer the question below to find the exact steps you need to take.
            Don't panic. You've got this, and we're here to help you through it.
          </p>
        </div>
      </section>

      {/* Scenarios Accordion */}
      <section className="py-16 bg-gray-50" data-id="element-966">
        <div className="max-w-4xl mx-auto px-6" data-id="element-967">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-968">
            What's Happening Right Now?
          </h2>
          <div className="space-y-4" data-id="element-969">
            {SCENARIOS.map(scenario => <ScenarioCard key={scenario.id} scenario={scenario} data-id="element-970" />)}
          </div>
        </div>
      </section>

      {/* Still Not Sure */}
      <section className="py-16 bg-white border-b border-gray-100" data-id="element-971">
        <div className="max-w-4xl mx-auto px-6" data-id="element-972">
          <div className="bg-[#f8f8f8] rounded-2xl p-8 md:p-12 border border-gray-200" data-id="element-973">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6" data-id="element-974">
              Still Not Sure What to Do?
            </h2>
            <p className="text-lg text-gray-600 mb-8" data-id="element-975">
              If your situation doesn't match any of these, or if you need more
              help:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-976">
              <ul className="space-y-4" data-id="element-977">
                <li className="flex items-start" data-id="element-978">
                  <CheckCircle className="text-[#631bb7] w-6 h-6 mr-3 flex-shrink-0" data-id="element-979" />
                  <span className="text-gray-700" data-id="element-980">
                    <strong data-id="element-981">Don't panic.</strong> Take a breath. Most digital
                    crises can be resolved if you act methodically.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-982">
                  <CheckCircle className="text-[#631bb7] w-6 h-6 mr-3 flex-shrink-0" data-id="element-983" />
                  <span className="text-gray-700" data-id="element-984">
                    <strong data-id="element-985">Start with your most critical accounts.</strong>{' '}
                    Secure your email first (it's the key to everything else),
                    then your bank and financial accounts.
                  </span>
                </li>
              </ul>
              <ul className="space-y-4" data-id="element-986">
                <li className="flex items-start" data-id="element-987">
                  <CheckCircle className="text-[#631bb7] w-6 h-6 mr-3 flex-shrink-0" data-id="element-988" />
                  <span className="text-gray-700" data-id="element-989">
                    <strong data-id="element-990">Contact Caveo support.</strong> We're here to help
                    you work through complicated situations and create a
                    recovery plan.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-991">
                  <CheckCircle className="text-[#631bb7] w-6 h-6 mr-3 flex-shrink-0" data-id="element-992" />
                  <span className="text-gray-700" data-id="element-993">
                    <strong data-id="element-994">Document everything.</strong> Take screenshots,
                    write down dates and times, and keep records of all
                    communications.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Resources */}
      <section className="py-16 bg-white" data-id="element-995">
        <div className="max-w-4xl mx-auto px-6" data-id="element-996">
          <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center" data-id="element-997">
            Important Resources
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-998">
            <div data-id="element-999">
              <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2" data-id="element-1000">
                Report Fraud & Get Help
              </h3>
              <ul className="space-y-3" data-id="element-1001">
                <li data-id="element-1002">
                  <a href="https://identitytheft.gov" target="_blank" rel="noopener noreferrer" className="flex items-center text-[#631bb7] hover:underline font-medium" data-id="element-1003">
                    FTC Identity Theft (IdentityTheft.gov)
                    <ExternalLink size={14} className="ml-1" data-id="element-1004" />
                  </a>
                </li>
                <li data-id="element-1005">
                  <a href="https://ic3.gov" target="_blank" rel="noopener noreferrer" className="flex items-center text-[#631bb7] hover:underline font-medium" data-id="element-1006">
                    FBI Internet Crime Complaint Center (IC3.gov)
                    <ExternalLink size={14} className="ml-1" data-id="element-1007" />
                  </a>
                </li>
                <li data-id="element-1008">
                  <a href="https://reportfraud.ftc.gov" target="_blank" rel="noopener noreferrer" className="flex items-center text-[#631bb7] hover:underline font-medium" data-id="element-1009">
                    Report Fraud (ReportFraud.ftc.gov)
                    <ExternalLink size={14} className="ml-1" data-id="element-1010" />
                  </a>
                </li>
                <li data-id="element-1011">
                  <a href="https://donotcall.gov" target="_blank" rel="noopener noreferrer" className="flex items-center text-[#631bb7] hover:underline font-medium" data-id="element-1012">
                    National Do Not Call Registry (DoNotCall.gov)
                    <ExternalLink size={14} className="ml-1" data-id="element-1013" />
                  </a>
                </li>
              </ul>
            </div>

            <div data-id="element-1014">
              <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2" data-id="element-1015">
                Credit Bureaus
              </h3>
              <ul className="space-y-3 text-gray-700" data-id="element-1016">
                <li data-id="element-1017">
                  <strong data-id="element-1018">Equifax:</strong> 800-685-1111
                </li>
                <li data-id="element-1019">
                  <strong data-id="element-1020">Experian:</strong> 888-397-3742
                </li>
                <li data-id="element-1021">
                  <strong data-id="element-1022">TransUnion:</strong> 888-909-8872
                </li>
              </ul>
            </div>

            <div data-id="element-1023">
              <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2" data-id="element-1024">
                Government Fraud Hotlines
              </h3>
              <ul className="space-y-3 text-gray-700" data-id="element-1025">
                <li data-id="element-1026">
                  <strong data-id="element-1027">Social Security Administration:</strong> 800-269-0271
                </li>
                <li data-id="element-1028">
                  <strong data-id="element-1029">IRS Identity Protection:</strong> 800-908-4490
                </li>
              </ul>
            </div>

            <div data-id="element-1030">
              <h3 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2" data-id="element-1031">
                Free Credit Reports
              </h3>
              <ul className="space-y-3" data-id="element-1032">
                <li data-id="element-1033">
                  <a href="https://annualcreditreport.com" target="_blank" rel="noopener noreferrer" className="flex items-center text-[#631bb7] hover:underline font-medium" data-id="element-1034">
                    AnnualCreditReport.com
                    <ExternalLink size={14} className="ml-1" data-id="element-1035" />
                  </a>
                  <span className="text-sm text-gray-500 block mt-1" data-id="element-1036">
                    Check weekly for free
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Prevention */}
      <section className="py-16 bg-[#1a0647] text-white" data-id="element-1037">
        <div className="max-w-4xl mx-auto px-6" data-id="element-1038">
          <div className="text-center mb-12" data-id="element-1039">
            <h2 className="text-3xl font-bold mb-4" data-id="element-1040">
              Prevention: Making This Less Likely to Happen Again
            </h2>
            <p className="text-lg text-gray-300" data-id="element-1041">
              Once you've handled the immediate crisis, these steps will help
              protect you going forward.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1042">
            {['Use unique passwords for every account. Password reuse is the #1 way one breach becomes many breaches.', "Turn on two-factor authentication everywhere it's offered. This stops most account takeovers even if your password is stolen.", "Be suspicious of urgency. Real banks, the IRS, and tech companies don't create artificial urgency or threaten you.", 'Verify requests independently. If someone contacts you claiming to be from a company, hang up and call the official number yourself.', "Don't click links in unexpected messages. Instead, go directly to the website or app.", 'Monitor your accounts regularly. Check your bank and credit card statements weekly. Sign up for transaction alerts.', 'Keep your software updated. Enable automatic updates on all devices.'].map((tip, idx) => <div key={idx} className="flex items-start bg-white bg-opacity-10 p-4 rounded-lg" data-id="element-1043">
                <CheckCircle className="text-[#00f4f8] w-5 h-5 mr-3 flex-shrink-0 mt-0.5" data-id="element-1044" />
                <span className="text-gray-200 text-sm leading-relaxed" data-id="element-1045">
                  {tip}
                </span>
              </div>)}
          </div>

          <div className="mt-12 text-center border-t border-white border-opacity-20 pt-8" data-id="element-1046">
            <p className="text-gray-300 italic" data-id="element-1047">
              "You're not alone. Over 73% of Americans have experienced some
              form of online scam or attack. What matters is how you respond.
              Taking quick, decisive action minimizes damage and helps you
              recover faster."
            </p>
          </div>
        </div>
      </section>

      <Footer data-id="element-1048" />
    </div>;
}